// src/routes.js
//
// Canonical route + workspace definitions (Pass 44 — account/workspace architecture).
// ONE source of truth for paths, the workspace each belongs to, home destinations,
// and legacy→canonical redirects. Given any pathname, `workspaceForPath` returns
// the workspace deterministically (independent of how the page was reached).
import { canEnterOrganizationWorkspace, canEnterInstructorWorkspace } from './utils/platformAccess';

export const WORKSPACES = {
  personal: { key: 'personal', label: 'My Coaching', short: 'Personal', color: '#2563EB', home: '/me' },
  instructor: { key: 'instructor', label: 'Instructor', short: 'Instructor', color: '#F05A1F', home: '/instructor' },
  organization: { key: 'organization', label: 'Operations', short: 'Ops', color: '#9333EA', home: '/organization' },
  // Pass 82D — authorized-staff READ-ONLY view of the real student experience (QA/inspection). NOT impersonation; the
  // label says "Student Preview", never "Student". Reuses the student blue but is a distinct workspace key + route.
  student_preview: { key: 'student_preview', label: 'Student Preview', short: 'Preview', color: '#2563EB', home: '/student-preview' },
  admin: { key: 'admin', label: 'Platform Admin', short: 'Admin', color: '#DC2626', home: '/admin' },
};

// Canonical route constants (used instead of scattered string literals).
export const ROUTES = {
  // cross-workspace
  login: '/login',
  account: '/account',            // universal account settings (identity only)
  notifications: '/notifications',
  // personal / coaching workspace
  me: '/me',
  meVideos: '/me/videos',
  meUpload: '/me/upload',
  meLessons: '/me/lessons',
  meProgress: '/me/progress',
  meMessages: '/me/messages',
  meSkills: '/me/skills',
  meCoachingProfile: '/me/coaching-profile',
  meBookings: '/me/bookings',
  discover: '/discover',
  // instructor workspace
  instructor: '/instructor',
  instructorSchedule: '/instructor/schedule',
  instructorClients: '/instructor/clients',
  instructorFeedback: '/instructor/feedback',
  instructorLessonBuilder: '/instructor/lesson-builder',
  instructorQuickLog: '/instructor/quick-lesson-log',
  instructorProfile: '/instructor/public-profile',
  instructorMessages: '/instructor/messages',
  // organisation workspace
  organization: '/organization',
  retention: '/retention',
  content: '/content/drills',
  // admin workspace
  admin: '/admin',
  adminContentPacks: '/platform/content-packs',
  adminBilling: '/platform/billing',
};

// Prefix → workspace. Order matters (most specific first).
const PREFIX_WORKSPACE = [
  ['/student-preview', 'student_preview'],
  ['/organization', 'organization'],
  ['/org/', 'organization'],
  ['/retention', 'organization'],
  ['/instructor', 'instructor'],
  ['/content/drills', 'instructor'],
  ['/platform/', 'admin'],
  ['/admin', 'admin'],
  ['/me', 'personal'],
  ['/dashboard/progress', 'personal'],
  ['/dashboard/videos', 'personal'],
  ['/dashboard/packages', 'personal'],
  ['/lessons', 'personal'],
  ['/my-skills', 'personal'],
  ['/upload', 'personal'],
  ['/messages', 'personal'],
  ['/my-bookings', 'personal'],
  ['/reviews', 'personal'],
  ['/discover', 'personal'],
];

// Determine the workspace for a pathname. `/account` is cross-workspace (returns null
// so the shell keeps the caller's current workspace). `/dashboard` is the role
// dispatcher and is resolved by the provider using capabilities, not the path.
export function workspaceForPath(pathname = '') {
  if (!pathname) return null;
  if (pathname === '/account' || pathname.startsWith('/account')) return null;
  for (const [prefix, ws] of PREFIX_WORKSPACE) {
    if (pathname === prefix || pathname.startsWith(prefix)) return ws;
  }
  return null;
}

// Legacy path → canonical path (exact-match redirects). Route params handled separately.
export const LEGACY_REDIRECTS = {
  '/dashboard/videos': ROUTES.meVideos,
  '/upload': ROUTES.meUpload,
  '/dashboard/progress': ROUTES.meProgress,
  '/lessons': ROUTES.meLessons,
  '/messages': ROUTES.meMessages,
  '/my-skills': ROUTES.meSkills,
  '/dashboard/packages': '/me/packages',
  '/schedule': ROUTES.instructorSchedule,
  '/instructor-feedback-center': ROUTES.instructorFeedback,
  '/notification-center': ROUTES.notifications,
  '/notifications-old': ROUTES.notifications,
};

// Legacy /dashboard → canonical workspace home (pure; used by DashboardResolver).
// Pass 58C — honor the chosen viewMode AND canonical entitlement, not only the legacy userData.role
// string, so a canonical ops user (no legacy role) lands in Operations and a dual-access user follows
// their selected workspace.
export function resolveDashboardHome(userData = {}, viewMode = '') {
  const role = userData?.role;
  const orgId = userData?.orgId || userData?.organizationId || '';
  const canOrg = canEnterOrganizationWorkspace(userData, orgId);
  const canInstruct = canEnterInstructorWorkspace(userData, orgId);
  if (role === 'admin') return WORKSPACES.admin.home;

  // Honor an explicitly chosen workspace ONLY when the user is actually entitled to it. A stale
  // saved viewMode (e.g. 'instructor' from a previous account state) must never route the user
  // into a workspace they can no longer enter.
  if (viewMode === 'organization' && canOrg) return WORKSPACES.organization.home;
  if (viewMode === 'instructor' && canInstruct) return WORKSPACES.instructor.home;

  // Pass 58F — CANONICAL entitlement WINS over the legacy top-level users.role string. A user with
  // an operable org membership who does not teach (e.g. John: organization_owner, isTeachingInstructor
  // false) lands in Operations and is NEVER bounced to /instructor by a stale users.role='instructor'.
  if (canOrg) return WORKSPACES.organization.home;
  if (canInstruct) return WORKSPACES.instructor.home;

  // Legacy fallback ONLY for un-migrated accounts with no canonical entitlement at all.
  if (role === 'organization') return WORKSPACES.organization.home;
  if (role === 'instructor') return WORKSPACES.instructor.home;
  return WORKSPACES.personal.home;
}

// Capability → available workspaces. Consumes the flat userData + helper booleans.
export function availableWorkspaces(userData = {}, { hasOrgAccess = false, isInstructor = false, isPlatformAdmin = false, canPreviewStudent = false } = {}) {
  const list = [];
  if (hasOrgAccess) list.push('organization');
  if (isInstructor) list.push('instructor');
  if (isPlatformAdmin) list.push('admin');
  // Pass 82D — authorized staff (org owner/admin, super-admin) get the read-only Student Preview workspace. This is
  // additive and does NOT grant "personal / My Coaching" (that stays genuine-student only, below).
  if (canPreviewStudent) list.push('student_preview');
  // Pass 58G — Personal / "My Coaching" requires a GENUINE student/personal relationship, not merely
  // being authenticated. A real student (role='student'), or a user who qualifies for nothing else (so
  // they always have a home). An org-only owner/ops (John/Serena) no longer sees My Coaching.
  const isStudent = userData?.role === 'student' || userData?.platformRole === 'student';
  if (isStudent || (!hasOrgAccess && !isInstructor && !isPlatformAdmin)) list.push('personal');
  return list;
}
